/**
 * RemotePlayers - 서버에서 받은 다른 플레이어를 화면에 그리기 위한 엔티티 관리
 * - EntityRegistry 에는 등록하지 않는다 (전투/AI/파티 이동 로직에 섞이지 않도록). 렌더러에만 전달된다.
 * - 위치는 서버 값으로 부드럽게 보간하고, 이동 방향/속도를 계산해 걷기 애니메이션을 만든다.
 */

import { EntityRegistry } from '../../core/EntityRegistry';
import { Direction8, EntityState } from '../../core/Types';
import { NetPlayerInfo } from '../../core/NetClient';

interface Remote {
  ent: EntityState;
  tx: number;
  ty: number;
}

function dirOf(dx: number, dy: number, prev: Direction8): Direction8 {
  if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) return prev;
  // 아이소 화면 좌표: x축 = 우하단, y축 = 좌하단
  const sx = dx - dy;
  const sy = (dx + dy) / 2;
  const idx = Math.round(Math.atan2(sy, sx) / (Math.PI / 4));
  const table: Record<number, Direction8> = { 0: 'E', 1: 'SE', 2: 'S', 3: 'SW', 4: 'W', '-4': 'W', '-3': 'NW', '-2': 'N', '-1': 'NE' } as any;
  return table[idx] ?? prev;
}

export class RemotePlayers {
  /** 렌더러가 참조하는 배열 (같은 배열 객체를 계속 갱신) */
  public readonly list: EntityState[] = [];
  private map = new Map<number, Remote>();

  constructor(private registry: EntityRegistry) {}

  private build(p: NetPlayerInfo): EntityState | null {
    const tpl = this.registry.getPlayer();
    if (!tpl) return null;
    const ent: EntityState = JSON.parse(JSON.stringify(tpl));
    ent.id = `remote_${p.id}`;
    ent.category = 'player';
    ent.targetId = null as any;
    (ent as any).cast = undefined;
    (ent as any).cc = undefined;
    (ent as any).dotEffect = undefined;
    ent.isDead = false;
    (ent as any).isAdmin = p.role === 'admin';
    this.applyInfo(ent, p);
    ent.vx = 0;
    ent.vy = 0;
    return ent;
  }

  private applyInfo(ent: EntityState, p: NetPlayerInfo): void {
    ent.name = p.name;
    ent.job = p.job;
    ent.role = p.role as any;
    ent.level = p.level;
    if (p.appearance) ent.appearance = p.appearance;
    ent.resources.maxHp = Math.max(1, p.maxHp);
    ent.resources.hp = Math.max(0, Math.min(ent.resources.maxHp, p.hp));
  }

  public upsert(p: NetPlayerInfo): void {
    const cur = this.map.get(p.id);
    if (cur) {
      this.applyInfo(cur.ent, p);
      cur.tx = p.x;
      cur.ty = p.y;
      return;
    }
    const ent = this.build(p);
    if (!ent) return;
    ent.x = p.x;
    ent.y = p.y;
    this.map.set(p.id, { ent, tx: p.x, ty: p.y });
    this.list.push(ent);
  }

  public replaceAll(players: NetPlayerInfo[]): void {
    this.clear();
    players.forEach((p) => this.upsert(p));
  }

  public remove(id: number): void {
    const r = this.map.get(id);
    if (!r) return;
    this.map.delete(id);
    const i = this.list.indexOf(r.ent);
    if (i >= 0) this.list.splice(i, 1);
  }

  public move(id: number, x: number, y: number, hp: number, maxHp: number): void {
    const r = this.map.get(id);
    if (!r) return;
    r.tx = x;
    r.ty = y;
    r.ent.resources.maxHp = Math.max(1, maxHp);
    r.ent.resources.hp = Math.max(0, Math.min(r.ent.resources.maxHp, hp));
  }

  public clear(): void {
    this.map.clear();
    this.list.length = 0;
  }

  public count(): number {
    return this.list.length;
  }

  public update(dt: number): void {
    const k = Math.min(1, dt * 12);
    for (const r of this.map.values()) {
      const e = r.ent;
      const dx = r.tx - e.x;
      const dy = r.ty - e.y;
      if (Math.abs(dx) > 12 || Math.abs(dy) > 12) {
        e.x = r.tx;
        e.y = r.ty;
        e.vx = 0;
        e.vy = 0;
        continue;
      }
      const nx = e.x + dx * k;
      const ny = e.y + dy * k;
      e.vx = dt > 0 ? (nx - e.x) / dt : 0;
      e.vy = dt > 0 ? (ny - e.y) / dt : 0;
      if (Math.hypot(dx, dy) < 0.02) {
        e.x = r.tx;
        e.y = r.ty;
        e.vx = 0;
        e.vy = 0;
      } else {
        e.x = nx;
        e.y = ny;
        e.dir = dirOf(dx, dy, e.dir);
      }
    }
  }
}
